/* PERSONALISED VIDEO LINKS — what the Admin page builds from a link.

   Pure functions, no DOM and no network, so they run under Node's test runner
   (landing/tests/video-links.test.ts) the same way they run in the page. The
   owner sends every email by hand from Gmail or a similar app; everything here
   is about making that paste quick and correct. */

const trimSlash = s => String(s || '').replace(/\/+$/, '');

/** The personal watch address for a token. */
export const watchUrl = (config, token) =>
  `${String(config.watch_base_url || '').replace(/\/?$/, '/')}${token}`;

/** A file on the media host, or '' when there is no host or no path. */
export const mediaUrl = (config, path) =>
  config.media_base_url && path ? `${trimSlash(config.media_base_url)}/${String(path).replace(/^\/+/, '')}` : '';

/** The first word of a name, for "Hi Anna". */
export const firstName = name => String(name || '').trim().split(/\s+/)[0] || '';

const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The campaign's own email text with {first_name}, {name} and {link} filled in. */
export function fillTemplate(template, { name, link }) {
  return String(template || '')
    .replace(/\{first_name\}/gi, firstName(name))
    .replace(/\{name\}/gi, String(name || ''))
    .replace(/\{link\}/gi, link);
}

/**
 * What lands on the clipboard for "Copy email": the thumbnail, wrapped in the
 * personal link, optionally under the owner's text. Both a rich (HTML) and a
 * plain version, since a paste target takes whichever it understands.
 *
 * @param {{link:string, thumbUrl?:string, text?:string, alt?:string, width?:number}} p
 */
export function emailClipboard({ link, thumbUrl = '', text = '', alt = 'Watch the video', width = 560 }) {
  const paragraphs = String(text || '').trim()
    ? String(text).trim().split(/\r?\n\r?\n/).map(p => `<p style="margin:0 0 14px">${esc(p).replace(/\r?\n/g, '<br>')}</p>`).join('')
    : '';
  const picture = thumbUrl
    ? `<a href="${esc(link)}" target="_blank" rel="noopener"><img src="${esc(thumbUrl)}" alt="${esc(alt)}" width="${width}" style="display:block;width:100%;max-width:${width}px;height:auto;border:0;border-radius:6px"></a>`
    : `<p><a href="${esc(link)}">${esc(link)}</a></p>`;
  const html = `<div>${paragraphs}${picture}</div>`;
  const plain = (String(text || '').trim() ? String(text).trim() + '\n\n' : '') + link;
  return { html, plain };
}

const csvCell = v => {
  const s = String(v ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** name, email, link — for a mail-merge add-on. */
export function recipientsCsv(links, config) {
  const rows = [['name', 'email', 'link'], ...links.map(l => [l.recipient_name, l.recipient_email, watchUrl(config, l.token)])];
  return rows.map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

/** "4:07" from seconds. */
export const clock = s => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

/** A title to start from, out of a file name: "spring_film-final.mp4" -> "Spring film final". */
export function titleFromFilename(name) {
  const base = String(name || '').replace(/\.[^.]*$/, '').replace(/[_.\-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
}

/**
 * Where the play button goes on a thumbnail of the given size: a circle in the
 * middle and a triangle inside it, optically centred (the triangle's centre of
 * mass sits a little left of its box, so it is nudged right to look centred).
 */
export function playButton(w, h) {
  const r = Math.round(Math.min(w, h) * 0.13);
  const cx = Math.round(w / 2), cy = Math.round(h / 2);
  const t = r * 0.9;                              // the triangle's height
  const x0 = cx - t * 0.34 + t * 0.1;
  return { cx, cy, r, tri: [[x0, cy - t / 2], [x0, cy + t / 2], [x0 + t * 0.86, cy]] };
}

/* ── what the Admin screen shows about a person ─────────────────────────── */

/** "AV" from "Anna Virtanen", "A" from "Anna", "?" from nothing. */
export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = [...parts[0]][0], last = parts.length > 1 ? [...parts[parts.length - 1]][0] : '';
  return (first + last).toUpperCase();
}

/** A stable hue (0-359) for a name, so a person keeps their colour. */
export function hueFor(text) {
  let h = 0;
  for (const ch of String(text || '')) h = (h * 31 + ch.codePointAt(0)) % 360;
  return h;
}

/** "just now", "12 min ago", "3 h ago", "yesterday", "4 days ago"; older than a
 *  week goes to `older(date)`, so the caller decides how a date is written. */
export function relativeTime(iso, now, older) {
  if (now == null) now = Date.now();
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 2) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return older ? older(new Date(t)) : new Date(t).toISOString().slice(0, 10);
}

/**
 * Where a person is in the story, as one word for a coloured pill. The furthest
 * stage wins: someone who finished is "Finished" whether or not they were also
 * marked as sent.
 */
export function linkStatus(l) {
  const s = l.stats || {};
  if (s.finished) return { key: 'finished', label: 'Finished' };
  if (s.started) return { key: 'watched', label: `Watched ${Math.round(s.furthest_pct || 0)}%` };
  if (s.visits > 0) return { key: 'opened', label: 'Opened' };
  if (l.status === 'sent') return { key: 'sent', label: 'Sent' };
  return { key: 'draft', label: 'Not sent' };
}

/** The filter chips over the recipient list. */
export const LINK_FILTERS = [
  ['all', 'All'], ['draft', 'Not sent'], ['waiting', 'Waiting'], ['opened', 'Opened'],
  ['watched', 'Watched'], ['finished', 'Finished'], ['cta', 'Pressed the button']
];

/** `waiting` is sent but not opened yet: the people worth a nudge. */
export function matchesFilter(l, filter) {
  const s = l.stats || {};
  switch (filter) {
    case 'draft': return l.status !== 'sent' && !(s.visits > 0);
    case 'waiting': return l.status === 'sent' && !(s.visits > 0);
    case 'opened': return s.visits > 0;
    case 'watched': return !!s.started;
    case 'finished': return !!s.finished;
    case 'cta': return (s.cta_clicks || 0) > 0;
    default: return true;
  }
}

/** Case-insensitive match on name or address; an empty query matches everyone. */
export function matchesQuery(l, query) {
  const q = String(query || '').trim().toLowerCase();
  return !q || String(l.recipient_name).toLowerCase().includes(q) || String(l.recipient_email).toLowerCase().includes(q);
}

/** People with recent activity first, then the rest in the order they were added. */
export function byRecentActivity(links) {
  return links.map((l, i) => [l, i]).sort((a, b) => {
    const x = a[0].stats?.last_activity_at || '', y = b[0].stats?.last_activity_at || '';
    if (x && y) return x < y ? 1 : x > y ? -1 : a[1] - b[1];
    if (x) return -1;
    if (y) return 1;
    return a[1] - b[1];
  }).map(p => p[0]);
}

/**
 * The retention chart's points: how many of those who pressed play were still
 * there at 0, 10 … 100 % of the video. The 100 % end is "finished", the others
 * come from the summary's drop-off. Returns [] when nobody pressed play.
 */
export function retentionPoints(summary) {
  if (!summary || !summary.started) return [];
  const pts = [...(summary.dropoff || [])];
  while (pts.length < 10) pts.push(0);
  pts.length = 10;
  pts.push(summary.finished || 0);
  return pts.map((n, i) => ({ pct: i * 10, n }));
}
